import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, UrlSerializer } from '@angular/router';
import { ta } from 'date-fns/locale';
import { TableMode } from 'src/app/components/grid/grid.component';
import { FieldDataType } from 'src/app/components/grid/FieldDataType';
import { ITableData } from 'src/app/components/grid/ITableData';
import { IColumn } from 'src/app/components/grid/IColumn';
import { GridUtil } from 'src/app/components/grid/GridUtil';
import { IExam, IFileDownload } from 'src/app/interface/dto';
import { EventService } from 'src/app/services/event-services/error-event.service';
import { ExamScheduleService } from 'src/app/services/exam-schedule-service';
import { ExamService } from 'src/app/services/exam-service.service';
import { SnackbarService } from 'src/app/services/snackbar.service';

@Component({
  selector: 'app-examinations',
  templateUrl: './examinations.component.html',
  styleUrls: ['./examinations.component.scss']
})
export class ExaminationsComponent implements OnInit {
  selectedExams: IExam[] = [];
  gridName = "examinationGrid";
  constructor(private examService: ExamService, private router: Router,
    private serializer: UrlSerializer, private snackBar: SnackbarService, private examSchService: ExamScheduleService, private route: ActivatedRoute, private eventService: EventService) {

  }

  updateGridParams(res: any) {
    if (!res) {
      return;
    }
    this.data.tableRows = [];
    const tableParams = { ...this.data.tableParams, pageInfo: res[0].pageInfo };
    this.data.tableParams = tableParams;

    // Transform the data to add the dynamic field
    const transformedRows = res.slice(1, res.length).map(row => ({
      ...row,
      maximumNumberOfAnswers: row.examinationTypeenum === 'SAQ'
        ? row.maximumNumberOfSAQAnswers
        : row.maximumNumberOfEssayAnswers
    }));

    this.data.tableRows.push(...transformedRows);

    setTimeout(() => {
      this.eventService.gridDataLoadedEvent.next(true);
    });
  }

  async ngOnInit() {
    this.setExam([]);
    this.route.queryParams.subscribe(async (params) => {
      let examGroupId = params['examGroupId'];
      if (Array.isArray(examGroupId)) {
        examGroupId = examGroupId[0];
      }
      if (examGroupId) {
        this.data.tableParams.filterParams.push({
          fieldName: 'examinationGroupID',
          fieldValue: examGroupId,
          queryOperand: 0,
          static: false
        });
        await this.refreshData(this.data.tableParams);
      }
    });

    this.examService.reloadGrid.subscribe((value) => {
      this.refreshData(this.data.tableParams);
    });
    await this.refreshData(this.data.tableParams);
  }

  setExam(exams: any[]) {
    this.selectedExams = exams;
    this.examService.setSelectedExam([...this.selectedExams]);
    this.examSchService.selectedIds = [];
    this.examSchService.selectedSchedules = [];
  }

  addExamClicked() {
    this.router.navigate(['create_examination/0']);
  }

  editExamClicked(event: any) {
    this.router.navigate([`create_examination/${this.examService.selectedExamIds[0]}`], {
      queryParams: { mode: 'edit' }
    });
  }

  async deleteExamClicked() {
    let data = {
      ids: this.examService.selectedExamIds,
      filterParams: this.examService.selectAll ? this.examService.filterParams : [],
      selectAll: this.examService.selectAll,
      excludedIds: GridUtil.getExcludedIds(this.gridName)
    };   
    await this.examService.deleteExam(data).then(async () => {
      await this.refreshData(this.data.tableParams);
      this.snackBar.success("Exam deleted successfully");
    });
  }

  async exportExamClicked() {
    const res = await this.examService.getExaminationExport(this.data.tableParams).then((res: IFileDownload) => {
      const link = document.createElement('a');
      link.setAttribute('target', '_blank');
      link.setAttribute('href', '' + 'data:application/octet-stream;base64,' + res.file);
      link.setAttribute('download', res.fileName + '.' + res.extension);
      document.body.appendChild(link);
      link.click();
      this.snackBar.success("Exam Exported successfully");
      link.remove();
    });

  }

  selectAllExamClicked() {
    this.examService.selectAll = !this.examService.selectAll;
    this.examSchService.selectAll = false;
    this.examService.filterParams = this.data.tableParams.filterParams;

  }
  data: ITableData = {
    tableRows: [],
    tableParams: {
      filterParams: [],
      sortParam: {
        fieldName: "",
        orderDirection: 0
      },
      pageInfo: {
        previousPageSize: 50,
        page: 1,
        pageSize: 50,
        recordCount: 0,
        pageCount: 0
      },
      tableMode: TableMode.serverProcessing,
      selectAll: false
    }
  };

  columns: IColumn[] = [
    {
      displayName: 'Name',
      fieldName: 'examinationName',
      fieldDataType: FieldDataType.text,
      searchable: true,
      orderable: true,

    },
    {
      displayName: 'ID',
      fieldName: 'id',
      fieldDataType: FieldDataType.text,
      searchable: true,
      orderable: true,

    },
    {
      displayName: 'Session',
      fieldName: 'session',
      fieldDataType: FieldDataType.text,
      searchable: true,
      orderable: true,

    },
    {
      displayName: 'Type',
      fieldName: 'examinationType',
      fieldDataType: FieldDataType.enum,
      searchable: true,
      orderable: true,

    },
    {
      displayName: 'Duration',
      fieldName: 'duration',
      fieldDataType: FieldDataType.text,
      searchable: true,
      orderable: true,

    },
    {
      displayName: 'Proctor Access Codes',
      fieldName: 'proctorAccessCodes',
      fieldDataType: FieldDataType.text,
      searchable: true,
      orderable: true,

    },
    {
      displayName: 'Show Score',
      fieldName: 'showScoreWhenFinished',
      fieldDataType: FieldDataType.boolean,
      searchable: true,
      orderable: true,

    },
    {
      displayName: 'Psychometric Test',
      fieldName: 'psychometricTest',
      fieldDataType: FieldDataType.boolean,
      searchable: true,
      orderable: true,
      
    },
    {
      displayName: 'Use Question Bank',
      fieldName: 'useQuestionBank',
      fieldDataType: FieldDataType.boolean,
      searchable: true,
      orderable: true,

    },
    {
      displayName: 'Use SEB',
      fieldName: 'useSEB',
      fieldDataType: FieldDataType.boolean,
      searchable: true,
      orderable: true,

    },
    {
      displayName: 'Number Of Questions',
      fieldName: 'numberOfQuestions',
      fieldDataType: FieldDataType.text,
      searchable: true,
      orderable: true,

    },
    {
      displayName: 'Maximum Number Of Answers',
      fieldName: 'maximumNumberOfAnswers',
      fieldDataType: FieldDataType.text,
      searchable: false,
      orderable: false,

    },
    {
      displayName: 'Question Selection Mode',
      fieldName: 'questionSelectionMode',
      fieldDataType: FieldDataType.text,
      searchable: true,
      orderable: true,

    },
    {
      displayName: 'Require Proctor Start',
      fieldName: 'requireProctorStart',
      fieldDataType: FieldDataType.boolean,
      searchable: true,
      orderable: true,

    },
    {
      displayName: 'Recording',
      fieldName: 'recording',
      fieldDataType: FieldDataType.boolean,
      searchable: true,
      orderable: true,

    },
    {
      displayName: 'Facial Recognition',
      fieldName: 'facialRecognition',
      fieldDataType: FieldDataType.boolean,
      searchable: true,
      orderable: true,

    }, {
      displayName: 'AI Proctoring',
      fieldName: 'aIProctoring',
      fieldDataType: FieldDataType.boolean,
      searchable: true,
      orderable: true,

    }, {
      displayName: 'Live Proctoring',
      fieldName: 'liveProctoring',
      fieldDataType: FieldDataType.boolean,
      searchable: true,
      orderable: true,

    },
    {
      displayName: 'No Primary Stream Policy',
      fieldName: 'noPrimaryStreamPolicy',
      fieldDataType: FieldDataType.enum,
      searchable: true,
      orderable: true,

    },
    {
      displayName: 'No Second View Stream Policy',
      fieldName: 'noSecondViewStreamPolicy',
      fieldDataType: FieldDataType.enum,
      searchable: true,
      orderable: true,

    },
    {
      displayName: 'Calculator Type',
      fieldName: 'calculatorType',
      fieldDataType: FieldDataType.enum,
      searchable: true,
      orderable: true,

    },
    {
      displayName: 'Start Date',
      fieldName: 'examStartDate',
      fieldDataType: FieldDataType.dateTime,
      searchable: true,
      orderable: true,

    },
    {
      displayName: 'End Date',
      fieldName: 'examEndDate',
      fieldDataType: FieldDataType.dateTime,
      searchable: true,
      orderable: true,

    },
    {
      displayName: 'Mock Exam',
      fieldName: 'mockExam',
      fieldDataType: FieldDataType.boolean,
      searchable: true,
      orderable: true,

    }, {
      displayName: 'Self Schedule',
      fieldName: 'selfSchedule',
      fieldDataType: FieldDataType.boolean,
      searchable: true,
      orderable: true,

    },
    {
      displayName: 'Mock Exam',
      fieldName: 'mockExam',
      fieldDataType: FieldDataType.boolean,
      searchable: true,
      orderable: true,

    },
    {
      displayName: 'Self Reschedule After',
      fieldName: 'selfRescheduleAfter',
      fieldDataType: FieldDataType.boolean,
      searchable: true,
      orderable: true,

    },
    {
      displayName: 'Randomise Options',
      fieldName: 'randomiseOptions',
      fieldDataType: FieldDataType.boolean,
      searchable: true,
      orderable: true,

    }, {
      displayName: 'Practice Exam',
      fieldName: 'practiceExam',
      fieldDataType: FieldDataType.boolean,
      searchable: true,
      orderable: true,

    }, {
      displayName: 'Max Retake',
      fieldName: 'maxRetake',
      fieldDataType: FieldDataType.number,
      searchable: true,
      orderable: true,

    }, {
      displayName: 'Require AdminStart',
      fieldName: 'requireAdminStart',
      fieldDataType: FieldDataType.boolean,
      searchable: true,
      orderable: true,

    }, {
      displayName: 'Display Question Text In QuestionList',
      fieldName: 'displayQuestionTextInQuestionList',
      fieldDataType: FieldDataType.boolean,
      searchable: true,
      orderable: true,

    }, {
      displayName: 'Show Submit Button On Every Question',
      fieldName: 'showSubmitButtonOnEveryQuestion',
      fieldDataType: FieldDataType.boolean,
      searchable: true,
      orderable: true,

    }, {
      displayName: 'Exam Paused',
      fieldName: 'examPaused',
      fieldDataType: FieldDataType.boolean,
      searchable: true,
      orderable: true,

    }, {
      displayName: 'Detect Focus Loss',
      fieldName: 'detectFocusLoss',
      fieldDataType: FieldDataType.boolean,
      searchable: true,
      orderable: true,

    }, {
      displayName: 'Passport Mandatory',
      fieldName: 'passportMandatory',
      fieldDataType: FieldDataType.boolean,
      searchable: true,
      orderable: true,

    }, {
      displayName: 'Skip AI Passport Check',
      fieldName: 'skipAIPassportCheck',
      fieldDataType: FieldDataType.boolean,
      searchable: true,
      orderable: true,

    }, {
      displayName: 'Use Captcha',
      fieldName: 'useCaptcha',
      fieldDataType: FieldDataType.boolean,
      searchable: true,
      orderable: true,

    }, {
      displayName: 'Lite Mode',
      fieldName: 'liteMode',
      fieldDataType: FieldDataType.boolean,
      searchable: true,
      orderable: true,

    }, {
      displayName: 'Allow Ticket',
      fieldName: 'allowComplaint',
      fieldDataType: FieldDataType.boolean,
      searchable: true,
      orderable: true,

    }, {
      displayName: 'Allow Exam Retake',
      fieldName: 'allowExamRetake',
      fieldDataType: FieldDataType.boolean,
      searchable: true,
      orderable: true,

    }, {
      displayName: 'Exclude From RDS Config  Calculation',
      fieldName: 'excludeFromRDSConfigCalculation',
      fieldDataType: FieldDataType.boolean,
      searchable: true,
      orderable: true,

    }, {
      displayName: 'Sectionalized Essay Marking',
      fieldName: 'sectionalizedEssayMarking',
      fieldDataType: FieldDataType.boolean,
      searchable: true,
      orderable: true,

    }, {
      displayName: 'Examination Duration Percentage',
      fieldName: 'examinationDurationPercentage',
      fieldDataType: FieldDataType.boolean,
      searchable: true,
      orderable: true,

    }, {
      displayName: 'Answered Questions Percentage',
      fieldName: 'answeredQuestionsPercentage',
      fieldDataType: FieldDataType.boolean,
      searchable: true,
      orderable: true,

    }, {
      displayName: 'Loss of Focus Restart Action Limit',
      fieldName: 'lossofFocusRestartActionLimit',
      fieldDataType: FieldDataType.boolean,
      searchable: true,
      orderable: true,

    },
    {
      displayName: 'Allow External Primary Webcam',
      fieldName: 'allowExternalPrimaryWebcam',
      fieldDataType: FieldDataType.boolean,
      searchable: true,
      orderable: true,

    },
    {
      displayName: 'Allow External Second View Webcam',
      fieldName: 'allowExternalSecondViewWebcam',
      fieldDataType: FieldDataType.boolean,
      searchable: true,
      orderable: true,

    },
    {
      displayName: 'Require Second View',
      fieldName: 'requireSecondView',
      fieldDataType: FieldDataType.boolean,
      searchable: true,
      orderable: true,

    },
    {
      displayName: 'Show Question List',
      fieldName: 'showQuestionList',
      fieldDataType: FieldDataType.boolean,
      searchable: true,
      orderable: true,

    },
    {
      displayName: 'Exam Group',
      fieldName: 'examinationGroupID',
      fieldDataType: FieldDataType.text,
      searchable: true,
      orderable: true,

    },
    {
      displayName: 'Enforce Schedule EndTime',
      fieldName: 'enforceScheduleEndTime',
      fieldDataType: FieldDataType.dateTime,
      searchable: true,
      orderable: true,

    },
    {
      displayName: 'Self Registration EndDate',
      fieldName: 'selfRegistrationEndDate',
      fieldDataType: FieldDataType.dateTime,
      searchable: true,
      orderable: true,

    },
    {
      displayName: 'Instructions HTML',
      fieldName: 'instructionsHTML',
      fieldDataType: FieldDataType.text,
      searchable: true,
      orderable: true,

    },
    {
      displayName: 'Post Exam Messages',
      fieldName: 'postExamMessages',
      fieldDataType: FieldDataType.text,
      searchable: true,
      orderable: true,

    },
    {
      displayName: 'Email Notification Template',
      fieldName: 'emailNotificationTemplate',
      fieldDataType: FieldDataType.text,
      searchable: true,
      orderable: true,

    },
  ];

  setExamData(rowIds: any[]) {
    if (!this.data?.tableRows?.length) { //Just in case tableRows might still not be ready
      setTimeout(() => this.setExamData(rowIds), 50);
      return;
    }
    let storedIds = GridUtil.getSelectedDataID(this.gridName) ?? [];
    const selectedExams: IExam[] = rowIds.map(x => this.data.tableRows[x] as IExam).filter(Boolean);
    let selectedDataRowsId = storedIds.length > 0 ? storedIds : selectedExams.map(x => x.id);
    GridUtil.setRowCliclk("examinationGrid", rowIds, selectedDataRowsId);
    this.setExam(selectedExams);
  }

  veiwAllExamData(examIds: any[]) {
    const selectedExams: any[] = [];
    examIds.forEach(x => selectedExams.push(this.data.tableRows[x]));
    this.setExam(selectedExams);
    this.router.navigate([`create_examination/${this.examService.selectedExamIds[0]}`], {
      queryParams: { mode: 'view-all' }
    });
  }
  async refreshData(tableParams) {
    const res = await this.examService.getExamination(tableParams) as any[];
    this.updateGridParams(res);
  }
  copyExamClicked(event: any) {
    this.router.navigate([`create_examination/${this.examService.selectedExamIds[0]}`], {
      queryParams: { mode: 'copy' }
    });
  }
}
